import { IDashboardStats } from "../../models/DashboardStats";
import PriceFormat from "../../utils/priceFormat";
import DashboardTile from "./DashboardTile";

interface IPartsManagementTableProps {
  dashboardStats: IDashboardStats | undefined;
  span?: 1 | 2;
}

type DashboardStatsExtended = IDashboardStats & {
  averagePartsSoldTodayValue?: number;
};

type RowConfig = { valueKey: keyof DashboardStatsExtended; label: string; bgClass: string; textClass: string; format?: "price" };

const rowConfig: RowConfig[] = [
  {
    valueKey: "partsSoldToday",
    label: "No. Parts Sold Today",
    bgClass: "bg-orange-50",
    textClass: "text-orange-700",
  },
  {
    valueKey: "partsSoldTodayPrice",
    label: "Value Parts Sold Today",
    bgClass: "bg-emerald-50",
    textClass: "text-emerald-700",
    format: "price",
  },
  {
    valueKey: "averagePartsSoldTodayValue",
    label: "Average Value Parts Sold Today",
    bgClass: "bg-teal-50",
    textClass: "text-teal-700",
    format: "price",
  },
  {
    valueKey: "counterSalesToday",
    label: "Counter Sales Today",
    bgClass: "bg-sky-50",
    textClass: "text-sky-700",
  },
  {
    valueKey: "ebaySalesToday",
    label: "Ebay Sales Today",
    bgClass: "bg-indigo-50",
    textClass: "text-indigo-700",
  },
  {
    valueKey: "partsRemovedToday",
    label: "No. Parts Dismantled Today",
    bgClass: "bg-amber-50",
    textClass: "text-amber-700",
  },
  {
    valueKey: "partsImagedToday",
    label: "No. Parts Imaged Today",
    bgClass: "bg-pink-50",
    textClass: "text-pink-700",
  },
  {
    valueKey: "partsLocatedToday",
    label: "No. Parts Located Today",
    bgClass: "bg-rose-50",
    textClass: "text-rose-700",
  },
  {
    valueKey: "partsListedToday",
    label: "No. Parts Listed Today",
    bgClass: "bg-lime-50",
    textClass: "text-lime-700",
  },
  {
    valueKey: "partsPickedToday",
    label: "No. Parts Picked Today",
    bgClass: "bg-cyan-50",
    textClass: "text-cyan-700",
  },
  {
    valueKey: "partsDispatchedToday",
    label: "No. Parts Shipped Today",
    bgClass: "bg-violet-50",
    textClass: "text-violet-700",
  },
];

function formatValue(value: number | null | undefined, asPrice?: boolean): string {
  if (value == null || Number.isNaN(value)) return "—";
  if (asPrice) return PriceFormat(value);
  return String(value);
}

export default function PartsManagementTable({ dashboardStats, span = 2 }: IPartsManagementTableProps) {
  if (!dashboardStats) return null;

  const stats = dashboardStats as DashboardStatsExtended;
  const averagePartsSoldTodayValue =
    (stats.partsSoldToday ?? 0) > 0 ? (stats.partsSoldTodayPrice ?? 0) / (stats.partsSoldToday ?? 0) : 0;

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <div className="mb-3 w-full">
          <h3 className="prose font-semibold text-gray-900">Parts Management</h3>
          <p className="text-sm text-gray-500 mt-0.5">Sales, dismantling and listing activity today</p>
        </div>

        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 shadow-sm min-h-[28rem] max-h-[36rem] overflow-y-auto">
          <table className="w-full border-collapse text-left text-base">
            <thead className="sticky top-0 bg-gray-100 z-10">
              <tr className="border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">Metric</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">Value</th>
              </tr>
            </thead>
            <tbody>
              {rowConfig.map((row, index) => {
                const value = row.valueKey === "averagePartsSoldTodayValue"
                  ? averagePartsSoldTodayValue
                  : (stats[row.valueKey] as number | null | undefined);
                return (
                  <tr key={index} className={`font-medium transition-colors ${row.bgClass}`}>
                    <td className={`py-2 pl-4 pr-4 ${row.textClass}`}>{row.label}</td>
                    <td className={`py-2 pr-4 text-right font-bold ${row.textClass}`}>
                      {formatValue(value, row.format === "price")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}
